import React from 'react'
import PropTypes from 'prop-types'
import {Flex, Stack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import BundleProductRow from './bundle-product-row'
import SanitizedHtml from '../../html-sanitizer'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import CurrentPrice from '../../display-price/current-price'
import ListPrice from '../../display-price/list-price'
import InformativeLabel from '../../informative-label/partials/InformativeLabel'
import Checkbox from '../../checkbox'

const FixedBundles = ({
    product,
    fixedBundles = [],
    mainProductSelection,
    onMainProductSelectionChange = () => {},
}) => {
    const {currency} = useCurrency()
    const style = useMultiStyleConfig('Bundle')
    const intl = useIntl()
    const isSingleMachineSelected =
        mainProductSelection?.type === 'single' && mainProductSelection?.id === product?.id
    return (
        <Stack {...style.fixedBundlesStack}>
            <Text {...style.fixedBundlesTitle}>
                <FormattedMessage id="fixedBundles.title" defaultMessage="Buy now" />
            </Text>
            <Flex {...style.fixedBundlesContainer}>
                {/* SINGLE MACHINE */}
                <Flex
                    {...style.optionCard}
                    {...(isSingleMachineSelected ? style.optionCardSelected : {})}
                    onClick={() =>
                        onMainProductSelectionChange({
                            type: 'single',
                            id: product?.id,
                        })
                    }
                >
                    <Flex {...style.optionCardHeader}>
                        <Text {...style.optionCardHeaderTitle}>
                            <FormattedMessage
                                id="fixedBundles.singleMachine"
                                defaultMessage="Single Machine"
                            />
                        </Text>
                        <Checkbox
                            variant="pdp-recommendation-checkbox"
                            isChecked={isSingleMachineSelected}
                            role="radio"
                            name="fixed-bundle-selection"
                            aria-checked={isSingleMachineSelected}
                            aria-label={intl.formatMessage({
                                id: 'fixedBundles.singleMachine',
                                defaultMessage: 'Single Machine',
                            })}
                            onChange={() =>
                                onMainProductSelectionChange({
                                    type: 'single',
                                    id: product?.id,
                                })
                            }
                        />
                    </Flex>
                    <Flex {...style.optionCardContent}>
                        <BundleProductRow product={product} showPrice={false} />
                    </Flex>
                    <Flex {...style.optionCardFooter}>
                        <Flex {...style.optionCardFooterContent}>
                            <Text>
                                <FormattedMessage id="fixedBundles.price" defaultMessage="Price" />
                            </Text>
                            <CurrentPrice
                                price={product?.price}
                                currency={currency}
                                sx={style.optionCardFooterPrice}
                            />
                        </Flex>
                    </Flex>
                </Flex>
                {/* FIXED BUNDLES */}
                {fixedBundles?.map((bundle, index) => {
                    const isSelected =
                        mainProductSelection?.type === 'fixedBundle' &&
                        mainProductSelection?.id === bundle.productSetID
                    const savedAmount =
                        Math.round((bundle.standardTotalPrice - bundle.totalBundlePrice) * 100) /
                        100

                    const savedAmountWithCurrency = intl.formatNumber(savedAmount, {
                        style: 'currency',
                        currency,
                    })
                    const isOutOfStock = bundle.productSetIsOutOfStock
                    return (
                        <Flex
                            key={index}
                            {...style.optionCard}
                            {...(isSelected ? style.optionCardSelected : {})}
                            aria-disabled={isOutOfStock}
                            onClick={() => {
                                if (isOutOfStock) {
                                    return
                                }

                                onMainProductSelectionChange({
                                    type: 'fixedBundle',
                                    id: bundle.productSetID,
                                })
                            }}
                        >
                            <Flex {...style.optionCardHeader}>
                                <Flex {...style.optionCardHeaderContainer}>
                                    <Text {...style.optionCardHeaderTitle}>
                                        {bundle.productSetName}
                                    </Text>
                                    <Text {...style.optionCardHeaderSubtitle}>
                                        <SanitizedHtml html={bundle.productSetDescription} />
                                    </Text>
                                </Flex>
                                <Checkbox
                                    variant="pdp-recommendation-checkbox"
                                    isChecked={isSelected}
                                    isDisabled={isOutOfStock}
                                    role="radio"
                                    name="fixed-bundle-selection"
                                    aria-checked={isSelected}
                                    aria-label={bundle.productSetName}
                                    onChange={() =>
                                        !isOutOfStock &&
                                        onMainProductSelectionChange({
                                            type: 'fixedBundle',
                                            id: bundle.productSetID,
                                        })
                                    }
                                />
                            </Flex>
                            {isSelected && (
                                <Flex {...style.optionCardContent}>
                                    {bundle.productSetProducts?.map((product, productIndex) => (
                                        <BundleProductRow key={productIndex} product={product} />
                                    ))}
                                </Flex>
                            )}
                            <Flex {...style.optionCardFooter}>
                                <Flex {...style.optionCardFooterContent}>
                                    <Flex {...style.optionBadgeContainer}>
                                        {isOutOfStock && (
                                            <InformativeLabel
                                                label={'OUT OF STOCK'}
                                                variant="stockInfo"
                                                height="22px"
                                            />
                                        )}
                                        {savedAmount > 0 && (
                                            <InformativeLabel
                                                /* PLACEHOLDER VERSION *\
                                                /* label={intl.formatMessage(
                                                    {
                                                        id: 'fixedBundle.savedAmountLabel',
                                                        defaultMessage:
                                                            'Save {savedAmountWithCurrency}',
                                                    },
                                                    {savedAmountWithCurrency},
                                                )} */
                                                label={`${intl.formatMessage({
                                                    id: 'fixedBundle.saveLabel',
                                                    defaultMessage: 'Save',
                                                })} ${savedAmountWithCurrency}`}
                                                variant="bundleSavings"
                                                height="22px"
                                            />
                                        )}
                                    </Flex>
                                    <Flex
                                        alignItems="baseline"
                                        justifyContent="space-between"
                                        gap="1px"
                                    >
                                        {bundle.standardTotalPrice > bundle.totalBundlePrice && (
                                            <ListPrice
                                                price={bundle.standardTotalPrice}
                                                currency={currency}
                                                sx={{
                                                    textStyle: 'body-xsmall',
                                                    color: 'foreground.neutral.subtle',
                                                }}
                                            />
                                        )}

                                        <CurrentPrice
                                            price={bundle.totalBundlePrice}
                                            currency={currency}
                                            sx={style.optionCardFooterPrice}
                                        />
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Flex>
                    )
                })}
            </Flex>
        </Stack>
    )
}

FixedBundles.propTypes = {
    product: PropTypes.object,
    fixedBundles: PropTypes.array,
    mainProductSelection: PropTypes.shape({
        type: PropTypes.string,
        id: PropTypes.string,
    }),
    onMainProductSelectionChange: PropTypes.func,
}

export default FixedBundles
